import React, { ReactNode } from 'react';

import { FlatButton, Pagination as UIPagination } from '@travel/ui';
import { cx } from '@travel/utils';

import { Translate } from 'core/translate';

import styles from './pagination.module.scss';

type Action = 'next' | 'previous';

type Props = {
  /** String to define wrapper style */
  className?: string;
  /** String to define pagination element style */
  paginationClassName?: string;
  /** Callback to be called when next/prev page action needed */
  callback?: (offset: number, limit: number, action?: Action) => void;
  /** Type to define custom style of pagination element */
  customStyle?: 'loadmore' | 'pagination' | 'nextAndPrev';
  /** Number to define the limit for next request */
  limit: number;
  /** Number to define start offset of the request */
  offset: number;
  /** Number of total to check whether the next page load more button should be displayed or not */
  total: number;
  /** Unit label */
  unit?: React.ReactNode;
  /** The type of navigation */
  navigationType?: 'light' | 'dark' | 'simple';
  /** the total number of item which show pagination without squeeze style*/
  itemWithoutSqueeze?: number;
  /** Custom flag to define whether there is next page or not.
   * In case that this props is undefined, we will use the default calculation
   * (for 'loadmore' and 'nextAndPrev' style).
   ***/
  hasNext?: boolean;
  /** Custom number to define the offset of next page.
   * In case that this props is undefined, we will use the default calculation
   * (for 'loadmore' and 'nextAndPrev' style).
   ***/
  nextPageOffset?: number;
  /** Custom flag to define whether there is previous page or not.
   * In case that this props is undefined, we will use the default calculation
   * (for 'nextAndPrev' style).
   ***/
  hasPrevious?: boolean;
  /** Custom number to define the offset of previous page.
   * In case that this props is undefined, we will use the default calculation
   * (for 'nextAndPrev' style).
   ***/
  previousPageOffset?: number;
  paginationLabelResolver?: (reviewCount: string, totalCount: number) => ReactNode;
};

function getValidData<T>(defaultData: T, customData?: T): T {
  return customData === undefined ? defaultData : customData;
}

function Pagination(props: Props) {
  const { customStyle = 'loadmore', paginationLabelResolver } = props;

  const total = Number(props.total);
  const offset = Number(props.offset);
  const limit = Number(props.limit);
  const hasNext = getValidData(offset + limit < total, props.hasNext);
  const hasPrev = getValidData(offset - limit > -1, props.hasPrevious);

  return (
    <div
      className={cx(props.className, styles.wrapper, styles[customStyle])}
      data-testid="pagination-wrapper"
    >
      {/* pagination */}
      {customStyle === 'pagination' && total > limit && (
        <UIPagination
          className={props.paginationClassName}
          total={total}
          offset={offset}
          limit={limit}
          onChangePage={props.callback}
          unit={props.unit}
          navigationType={props.navigationType}
          itemWithoutSqueeze={props.itemWithoutSqueeze}
          paginationLabelResolver={paginationLabelResolver}
        />
      )}
      {/* load more */}
      {hasNext && customStyle === 'loadmore' && (
        <FlatButton
          data-testid="pagination-loadmore-button"
          className={cx(props.paginationClassName, styles.loadmoreButton)}
          classType="secondary"
          onClick={() =>
            props.callback?.(getValidData(offset + limit, props.nextPageOffset), limit, 'next')
          }
        >
          <Translate id="Traveler_Common.Load_More" />
        </FlatButton>
      )}

      {/* next and prev */}
      {customStyle === 'nextAndPrev' && (
        <>
          {hasPrev && (
            <FlatButton
              data-testid="pagination-previous-button"
              className={cx(props.paginationClassName, styles.loadmoreButton)}
              classType="secondary"
              onClick={() =>
                props.callback?.(
                  getValidData(offset + limit, props.previousPageOffset),
                  limit,
                  'previous',
                )
              }
            >
              <Translate id="Traveler_Common.Previous" />
            </FlatButton>
          )}
          {hasNext && (
            <FlatButton
              data-testid="pagination-next-button"
              className={cx(props.paginationClassName, styles.loadmoreButton, styles.leftAuto)}
              classType="secondary"
              onClick={() =>
                props.callback?.(getValidData(offset - limit, props.nextPageOffset), limit, 'next')
              }
            >
              <Translate id="Traveler_Common.Next" />
            </FlatButton>
          )}
        </>
      )}
    </div>
  );
}

export default Pagination;
